import React from 'react';

import moment from 'moment';

import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';

import { EmailMessage } from '../messages/legacy-components/email-message';
import { TextMessage } from '../messages/legacy-components/text-message';

import { formatDateTime, formatRelativeDate } from '../util/formatters';
import { ICON_NAMES, MaterialDesignIcon } from '../util/material-design-icons';
import { MESSAGE_TYPE } from '../util/message-types';

export const LegacyScheduledText = ({
  scheduledMessage,
  onEditMessage,
  onCancelMessage,
  urlFileMappings,
}: {
  scheduledMessage: ScheduledMessage;
  onEditMessage?: () => void;
  onCancelMessage?: () => void;
  urlFileMappings?: UrlFileMapping[];
}) => (
  <TextMessage
    className="u-width-100"
    senderName={scheduledMessage.senderName || 'Team Engine (automatically)'}
    sendDateTime={scheduledMessage.sendDateTime}
    messageBody={scheduledMessage.message}
    announcement={scheduledMessage.Announcement}
    // All job applicant scheduled messages are automated
    // Start date triggered messages are also automated
    isAutomated={
      scheduledMessage.jobApplicantId !== null ||
      scheduledMessage.startDateTriggeredCommunicationId !== null
    }
    onEditMessage={onEditMessage}
    onCancelMessage={onCancelMessage}
    allowLinkToAnnouncement
    urlFileMappings={urlFileMappings}
    sentWithVCard={
      scheduledMessage.hasVCardAttached || scheduledMessage.Announcement?.hasVCardAttached
    }
    orgId={scheduledMessage.Announcement?.orgId}
    isScheduledToSend
  />
);

export const LegacyScheduledEmail = ({
  scheduledMessage,
  onEditMessage,
  onCancelMessage,
}: {
  scheduledMessage: ScheduledMessage;
  onEditMessage?: () => void;
  onCancelMessage?: () => void;
}) => (
  <EmailMessage
    className="u-width-100"
    senderName={scheduledMessage.senderName || 'Team Engine (automatically)'}
    sendDateTime={scheduledMessage.sendDateTime}
    messageBody={scheduledMessage.message}
    subject={scheduledMessage.subject || ''}
    announcement={scheduledMessage.Announcement}
    // All job applicant scheduled messages are automated
    // Start date triggered messages are also automated
    isAutomated={
      scheduledMessage.jobApplicantId !== null ||
      scheduledMessage.startDateTriggeredCommunicationId !== null
    }
    onEditMessage={onEditMessage}
    onCancelMessage={onCancelMessage}
    allowLinkToAnnouncement
  />
);

export const LegacyAnnouncementText = ({
  announcement,
  onEditMessage,
  onCancelMessage,
  urlFileMappings,
  announcementGroupLabelOnClick,
  isScheduledToSend,
}: {
  announcement: Announcement;
  onEditMessage?: () => void;
  onCancelMessage?: () => void;
  urlFileMappings?: UrlFileMapping[];
  announcementGroupLabelOnClick?: () => void;
  isScheduledToSend?: boolean;
}) => (
  <TextMessage
    className="u-width-100"
    senderName={`${announcement.senderName} to ${announcement.recipientCount} employee${
      announcement.recipientCount === 1 ? '' : 's'
    }`}
    sendDateTime={announcement.sentAt}
    messageBody={announcement.messageBody}
    announcement={announcement}
    isAnnouncement
    otherLanguages={
      announcement.spanishMessageBody
        ? [{ languageCode: 'es', messageBody: announcement.spanishMessageBody }]
        : undefined
    }
    onEditMessage={onEditMessage}
    onCancelMessage={onCancelMessage}
    urlFileMappings={urlFileMappings}
    announcementGroupLabelOnClick={announcementGroupLabelOnClick}
    sentWithVCard={announcement.hasVCardAttached}
    orgId={announcement.orgId}
    isScheduledToSend={isScheduledToSend}
    dataCy={`announcement-${announcement.announcementId}`}
  />
);

export const LegacyAnnouncementEmail = ({
  announcement,
  onEditMessage,
  onCancelMessage,
}: {
  announcement: Announcement;
  onEditMessage?: () => void;
  onCancelMessage?: () => void;
}) => (
  <EmailMessage
    className="u-width-100"
    senderName={announcement.senderName}
    recipientName={`${announcement.recipientCount} employee${
      announcement.recipientCount === 1 ? '' : 's'
    }`}
    sendDateTime={announcement.sentAt}
    messageBody={announcement.messageBody}
    subject={announcement.subject || ''}
    otherLanguages={
      announcement.spanishMessageBody && announcement.spanishSubject
        ? [
            {
              languageCode: 'es',
              messageBody: announcement.spanishMessageBody,
              subject: announcement.spanishSubject,
            },
          ]
        : undefined
    }
    announcement={announcement}
    isAnnouncement
    onEditMessage={onEditMessage}
    onCancelMessage={onCancelMessage}
    dataCy={`announcement-${announcement.announcementId}`}
  />
);

interface PendingMessageProps<T> {
  title: string;
  defaultExpanded?: boolean;
  icon: React.ReactNode;
  messages: Array<T>;
  onEditMessageGenerator?: (message: T) => () => void;
  onCancelMessageGenerator?: (message: T) => () => void;
  urlFileMappings?: Record<number, Array<UrlFileMapping>>;
}

const LegacyPendingMessageSection = ({
  title,
  icon,
  messages,
  type,
  onEditMessageGenerator,
  onCancelMessageGenerator,
  defaultExpanded,
  urlFileMappings,
}: PendingMessageProps<any> & { type: 'announcement' | 'scheduledMessage' }) =>
  messages.length > 0 ? (
    <Accordion className="u-margin-bottom-md" defaultExpanded={defaultExpanded}>
      <AccordionSummary
        expandIcon={<MaterialDesignIcon iconName={ICON_NAMES.EXPAND_MORE} />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className="u-spread">
          {icon}
          <div className="u-margin-left-sm">
            {title} ({messages.length})
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className="u-flexDirection-column">
        {messages.map((m) => {
          let onEditMessage = onEditMessageGenerator ? onEditMessageGenerator(m) : undefined;
          let onCancelMessage = onCancelMessageGenerator ? onCancelMessageGenerator(m) : undefined;
          // If this is displaying scheduled messages, only allow non-announcement messages to be
          // edited or canceled
          if (type === 'scheduledMessage' && m.announcementId !== null) {
            onEditMessage = undefined;
            onCancelMessage = undefined;
          }
          let EmailComponent;
          let TextComponent;
          let sendTime;
          let key;
          if (type === 'announcement') {
            key = `announcement-${m.announcementId}`;
            EmailComponent = (
              <LegacyAnnouncementEmail
                announcement={m}
                onEditMessage={onEditMessage}
                onCancelMessage={onCancelMessage}
              />
            );
            TextComponent = (
              <LegacyAnnouncementText
                announcement={m}
                onEditMessage={onEditMessage}
                onCancelMessage={onCancelMessage}
                urlFileMappings={urlFileMappings && urlFileMappings[m.announcementId]}
                isScheduledToSend
              />
            );
            sendTime = moment.utc(m.sentAt);
          } else {
            key = `scheduled-message-${m.scheduledMessageId}`;
            EmailComponent = (
              <LegacyScheduledEmail
                scheduledMessage={m}
                onEditMessage={onEditMessage}
                onCancelMessage={onCancelMessage}
              />
            );
            TextComponent = (
              <LegacyScheduledText
                scheduledMessage={m}
                onEditMessage={onEditMessage}
                onCancelMessage={onCancelMessage}
                urlFileMappings={urlFileMappings && urlFileMappings[m.announcementId]}
              />
            );
            sendTime = moment.utc(m.sendDateTime);
          }

          return (
            <React.Fragment key={key}>
              {sendTime.isAfter(moment()) ? (
                <div>
                  <span className="typo-bold">Sending {formatRelativeDate(sendTime)}</span>, on{' '}
                  {formatDateTime(sendTime.toDate())}.
                </div>
              ) : (
                <div className="typo-bold">Sending now...</div>
              )}
              {m.messageType === MESSAGE_TYPE.EMAIL ? EmailComponent : TextComponent}
            </React.Fragment>
          );
        })}
      </AccordionDetails>
    </Accordion>
  ) : null;

export const LegacyPendingAnnouncementSection = (props: PendingMessageProps<Announcement>) => (
  <LegacyPendingMessageSection {...props} type="announcement" />
);

export const LegacyPendingScheduledMessageSection = (
  props: PendingMessageProps<ScheduledMessage>,
) => <LegacyPendingMessageSection {...props} type="scheduledMessage" />;
